import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginUser, getMe } from '../services/auth';
import { safeErrorMessage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await loginUser({ username, password });
      // Le cookie httpOnly est pose automatiquement par le serveur
      const meResp = await getMe();
      login(meResp.data);
      if (data.must_change_password) {
        // TODO: rediriger vers page de changement de mot de passe
      }
      navigate('/');
    } catch (err) {
      let detail;
      if (err.response?.status === 429) {
        detail = 'Trop de tentatives de connexion. Veuillez réessayer dans une minute.';
      } else {
        detail = safeErrorMessage(err, 'Erreur de connexion');
      }
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* GAUCHE — identite Alton */}
      <div className="login-brand-side">
        <div className="login-aurora" aria-hidden="true">
          <span className="login-aurora-a"></span>
          <span className="login-aurora-b"></span>
        </div>

        <div className="login-brand-content">
          <img src="/alton-logo-inverted.svg" alt="Alton" className="login-logo" />

          <div className="login-tagline">
            <span className="login-ai-badge">AI</span>
            <span>Gestion intelligente des tests</span>
          </div>

          <div className="login-features">
            <div className="login-feature-item">
              <span className="login-feature-dot"></span>
              <span>Gestion des exigences et cas de test</span>
            </div>
            <div className="login-feature-item">
              <span className="login-feature-dot"></span>
              <span>Generation automatique par IA</span>
            </div>
            <div className="login-feature-item">
              <span className="login-feature-dot"></span>
              <span>Export Excel et suivi en temps reel</span>
            </div>
            <div className="login-feature-item">
              <span className="login-feature-dot"></span>
              <span>Matrice de tracabilite complete</span>
            </div>
          </div>
        </div>
      </div>

      {/* DROITE — formulaire */}
      <div className="login-form-side">
        <div className="login-form-wrapper">
          <div className="login-form-header">
            <h2 className="login-title">Connexion</h2>
            <p className="login-subtitle">Accedez a votre espace de travail</p>
          </div>

          {error && (
            <div className="login-error">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M8 4.5v4M8 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="login-field">
              <label className="login-label" htmlFor="field-login-username">Email ou nom d'utilisateur</label>
              <div className="login-input-wrapper">
                <svg className="login-input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                <input
                  id="field-login-username"
                  type="text"
                  className="login-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="admin@alton.local"
                  required
                  autoFocus
                />
              </div>
            </div>

            <div className="login-field">
              <label className="login-label" htmlFor="field-login-password">Mot de passe</label>
              <div className="login-input-wrapper">
                <svg className="login-input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <input
                  id="field-login-password"
                  type={showPassword ? 'text' : 'password'}
                  className="login-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Entrez votre mot de passe"
                  required
                />
                <button
                  type="button"
                  className="login-toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="login-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <output className="spinner-border spinner-border-sm me-2" />
                  Connexion en cours...
                </>
              ) : (
                <>
                  Se connecter
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: 8 }}>
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </>
              )}
            </button>
          </form>

          <div className="login-footer-text">
            Alton — Cahier de Test v1.0.0
          </div>
        </div>
      </div>
    </div>
  );
}
